import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Volume2, VolumeX } from 'lucide-react';

const AudioPlayer = () => {
  const [isMuted, setIsMuted] = useState(false);
  const [, setIsReady] = useState(false);
  const [pausedByPage, setPausedByPage] = useState(false);
  const [activeVideos, setActiveVideos] = useState(0);
  const audioRef = useRef(null);
  const wasAutoPausedByVideo = useRef(false);
  const location = useLocation();
  const isOnMusicPage = location.pathname === '/music';

  // Pause/resume on /music route
  useEffect(() => {
    if (!audioRef.current) return;
    if (isOnMusicPage) {
      audioRef.current.pause();
      setPausedByPage(true);
    } else if (pausedByPage && !isMuted) {
      audioRef.current.volume = 0.3;
      audioRef.current.play().catch(() => {});
      setPausedByPage(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnMusicPage]);

  // Initial play with fade-in (autoplay may be blocked)
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = 0.3;
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsReady(true);
            fadeIn();
          })
          .catch(() => {
            setIsReady(false);
          });
      }
    }
  }, []);

  // Global media listener — pause background music when ANY video OR audio plays
  useEffect(() => {
    const isMedia = (el) =>
      el && (el.tagName === 'VIDEO' || el.tagName === 'AUDIO') && el !== audioRef.current;
    const onMediaPlay = (e) => {
      if (isMedia(e.target)) {
        setActiveVideos((c) => c + 1);
        if (audioRef.current && !audioRef.current.paused) {
          audioRef.current.pause();
          wasAutoPausedByVideo.current = true;
        }
      }
    };
    const onMediaStop = (e) => {
      if (isMedia(e.target)) {
        setActiveVideos((c) => Math.max(0, c - 1));
      }
    };
    // Use capture so we catch events even if the media stops bubbling
    document.addEventListener('play', onMediaPlay, true);
    document.addEventListener('pause', onMediaStop, true);
    document.addEventListener('ended', onMediaStop, true);
    return () => {
      document.removeEventListener('play', onMediaPlay, true);
      document.removeEventListener('pause', onMediaStop, true);
      document.removeEventListener('ended', onMediaStop, true);
    };
  }, []);

  // YouTube/Vimeo iframe detection — iframes don't fire native play events.
  // When the user interacts with an iframe (clicks play), the browser shifts
  // focus to it and the parent window fires `blur`. If a video iframe is in
  // the viewport at that moment, treat it as "video started" and pause music.
  useEffect(() => {
    const isVideoIframeVisible = () => {
      const iframes = Array.from(document.querySelectorAll('iframe'));
      return iframes.some((f) => {
        const src = (f.src || '').toLowerCase();
        const isVideo = /youtube\.com|youtu\.be|vimeo\.com/.test(src);
        if (!isVideo) return false;
        const rect = f.getBoundingClientRect();
        return (
          rect.bottom > 0 &&
          rect.top < window.innerHeight &&
          rect.right > 0 &&
          rect.left < window.innerWidth
        );
      });
    };

    const onWindowBlur = () => {
      if (
        isVideoIframeVisible() &&
        audioRef.current &&
        !audioRef.current.paused
      ) {
        audioRef.current.pause();
        wasAutoPausedByVideo.current = true;
      }
    };

    window.addEventListener('blur', onWindowBlur);
    return () => window.removeEventListener('blur', onWindowBlur);
  }, []);

  // Resume background music when all videos are stopped
  useEffect(() => {
    if (
      activeVideos === 0 &&
      wasAutoPausedByVideo.current &&
      !isMuted &&
      !isOnMusicPage &&
      audioRef.current
    ) {
      audioRef.current.volume = 0.3;
      audioRef.current.play().catch(() => {});
      wasAutoPausedByVideo.current = false;
    }
  }, [activeVideos, isMuted, isOnMusicPage]);

  const fadeIn = () => {
    if (!audioRef.current) return;
    let volume = 0;
    const targetVolume = 0.3;
    const fadeInterval = setInterval(() => {
      if (volume < targetVolume) {
        volume += 0.01;
        audioRef.current.volume = Math.min(volume, targetVolume);
      } else {
        clearInterval(fadeInterval);
      }
    }, 100);
  };

  const toggleMute = () => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.volume = 0.3;
        audioRef.current.play().catch(() => {});
      } else {
        audioRef.current.volume = 0;
      }
      setIsMuted(!isMuted);
    }
  };

  if (isOnMusicPage) return null;

  return (
    <div className="audio-player-container">
      <audio
        ref={audioRef}
        loop
        preload="auto"
      >
        <source src="https://customer-assets.emergentagent.com/job_alchemy-village/artifacts/yvv46xtl_The%20Alchemy%20Village.mp3" type="audio/mpeg" />
      </audio>

      <button
        onClick={toggleMute}
        className="audio-toggle"
        data-testid="audio-toggle"
        aria-label={isMuted ? "Unmute audio" : "Mute audio"}
      >
        {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
      </button>
    </div>
  );
};

export default AudioPlayer;
